import { useAppDispatch, useAppSelector } from '@store/hooks';
import { onCommonNotificationRaised, setReserveCancelModalStatus } from '@store/slices/salesWizardSlice';
import { setOrderReserveStatus } from '@store/thunks';
import { IOrder, NextErrorCode, VehicleReserveStatus } from 'next-common';
import { ReserveButtonStages, TMessageBarSeverity } from 'types';
import { INotificationType } from '@common/interfaces';
import { selectIsReserveLoading } from '@store/selectors/salesWizardSelector';
import { useUnifiedNavigate } from '../useUnifiedNavigation';
import { Routes } from '@common';

export const useOnReserveOrCancelOrder = () => {
  const dispatch = useAppDispatch();
  const router = useUnifiedNavigate();
  const isReserveLoading = useAppSelector(selectIsReserveLoading);

  const raiseReservationFailureNotification = () => {
    dispatch(
      onCommonNotificationRaised({
        key: 'reservedFail',
        type: INotificationType.RESERVED_FAIL,
        errorCode: NextErrorCode.VEHICLE_RESERVE_ERROR,
        severityLevel: TMessageBarSeverity.ERROR,
      })
    );
  };

  const reserveOrCancelOrder = async (
    order: IOrder,
    vehicleReserveStatus: VehicleReserveStatus,
    skipNextReservation = false
  ) => {
    try {
      const reservation = await dispatch(
        setOrderReserveStatus({ vehicleReserveStatus, skipNextReservation })
      )?.unwrap();
      if (reservation) {
        router(Routes.getViewPage(order?.id));
      }
    } catch (error) {
      console.error('Reservation action failed', error);
      raiseReservationFailureNotification();
    }
  };

  const onReserveOrCancelOrder = async (order: IOrder, reserveButtonStage: ReserveButtonStages) => {
    if (reserveButtonStage === ReserveButtonStages.RESERVED) {
      dispatch(setReserveCancelModalStatus(true));
    }
    if (reserveButtonStage === ReserveButtonStages.NO_RESERVE_REQUEST) {
      await reserveOrCancelOrder(order, VehicleReserveStatus.WAITING, false);
    }
  };

  const confirmReserveCancel = async (order?: IOrder) => {
    await reserveOrCancelOrder(order, VehicleReserveStatus.WAITING_CANCELLATION, true);
  };

  return { onReserveOrCancelOrder, confirmReserveCancel, isReserveLoading };
};
